import { Suspense, useEffect, useMemo, useState } from "react";

import { CalculationResult, ComponentLegendProps } from "src/types";
import * as SubstanceMinMax from "src/utils/substance-min-max";
import { CalculationResultWMSTileLayer } from "./CalculationResultWMSTileLayer";
import { useLegend } from "src/hooks/useLegend";
import { LegendLoading } from "./legend/ LegendLoading";
import { LegendFront } from "./legend/LegendFront";
import { LegendBack } from "./legend/LegendBack";

export enum LegendType {
  default,
  dynamic,
  minMax,
}

interface Props {
  calculationResult: CalculationResult;
}

export function CalculationResultOnMap(props: Props) {
  const { calculationResult } = props;
  const { substanceId } = calculationResult;

  const [legendType, setLegendType] = useState(LegendType.dynamic);

  const { data: legend } = useLegend(props.calculationResult.dynamicLegend);
  const [minMaxList, setMinMaxList] = useState(SubstanceMinMax.create());

  /**
   * Memo's
   */
  const substanceMinMax = useMemo(() => SubstanceMinMax.get(minMaxList, substanceId), [minMaxList, substanceId]);
  const legendPath = useMemo(() => {
    switch (legendType) {
      case LegendType.default:
        return calculationResult.defaultLegend;
      case LegendType.dynamic:
        return calculationResult.dynamicLegend;
      case LegendType.minMax:
        const minMax = SubstanceMinMax.get(minMaxList, substanceId);

        if (!minMax) return false;

        const customLegendPath = calculationResult.minMaxLegend
          .replace("{0}", minMax.min.toString())
          .replace("{1}", minMax.max.toString());
        return customLegendPath;
    }
  }, [calculationResult, legendType, substanceId, minMaxList]);

  const legendFront = useMemo(
    () => (props: ComponentLegendProps) => {
      if (!legendPath) return null;

      return (
        <Suspense fallback={<LegendLoading />}>
          <LegendFront legendPath={legendPath} />
        </Suspense>
      );
    },
    [legendPath]
  );
  const legendBack = useMemo(() => {
    return (props: ComponentLegendProps) => {
      if (!legendPath) return null;

      function handleChooseDefault() {
        setLegendType(LegendType.default);
      }

      function handleChooseCustom(values: { min: number; max: number }) {
        setLegendType(LegendType.minMax);
        setMinMaxList(SubstanceMinMax.set(minMaxList, substanceId, values));
      }

      function handleChooseDynamic() {
        setLegendType(LegendType.dynamic);
      }

      return (
        <LegendBack
          legendType={legendType}
          customMinMax={substanceMinMax}
          onChooseDefault={handleChooseDefault}
          onChooseDynamic={handleChooseDynamic}
          onChooseCustom={handleChooseCustom}
        />
      );
    };
  }, [legendPath, legendType, minMaxList, substanceId, substanceMinMax]);

  /**
   * Effects
   */
  useEffect(setInitialLegendMinMax, [legend, minMaxList, substanceId]);

  /**
   * Functions
   */
  function setInitialLegendMinMax() {
    if (!legend) return;

    const minMax = { min: legend.min, max: legend.max };
    setMinMaxList(SubstanceMinMax.setOnlyInitial(minMaxList, substanceId, minMax));
  }

  // render the compoent
  return render();

  function render() {
    if (!legend) return null;
    if (!legendPath) return null;

    if (!substanceMinMax) return null;

    return (
      <CalculationResultWMSTileLayer
        calculationResult={calculationResult}
        legendPath={legendPath}
        legendFront={legendFront}
        legendBack={legendBack}
      />
    );
  }
}
